import { cloneDeep, maxBy, minBy } from 'lodash';
import { getRotatePosition } from '@/utils/collision-kit';
import { getLayerRuntimeValue } from '@/utils/layer-kit';
import {
    ILayerProps,
    IMarkupLabel,
    IMarkupLayerProps,
    IPoint,
    ISize,
    MarkupLabelType,
    MarkupLabelUnit,
    MarkupTextPositionType,
    MarkupType,
} from '@/version-types';
import { MARKUP_POINT_SIZE } from './layer';

class LabelKit {
    private dom: HTMLDivElement | null;
    private MIN_FONT_SIZE = 12;

    constructor() {
        this.dom = null;
    }

    private create() {
        this.dom = document.createElement('div');
        this.dom.style.position = 'absolute';
        this.dom.style.left = '0';
        this.dom.style.top = '0';
        this.dom.style.visibility = 'hidden';
        this.dom.style.pointerEvents = 'none';
        document.body.appendChild(this.dom);
    }

    static getLabelSize(layer: ILayerProps) {
        const { label } = layer.data as IMarkupLayerProps;
        const { paddingH, paddingV } = LabelKit.getPadding(layer);
        if (!LabelKit.getRenderText(label)) return { width: 0, height: 0, paddingH, paddingV };
        // console.log({ paddingH, paddingV, width: label.width, height: label.height });
        // 加上背景的padding，文字区域的真实宽度
        const width = label.width + paddingH * 2;
        const height = label.height + paddingV * 2;
        return {
            width,
            height,
            paddingH,
            paddingV,
        };
    }

    static getPadding(layer: ILayerProps, fontSize?: number) {
        const { label } = layer.data as IMarkupLayerProps;
        if (!LabelKit.getRenderText(label)) return { paddingH: 0, paddingV: 0 };
        const markupType = getLayerRuntimeValue<MarkupType>(layer, 'markupType');
        const baseFontSize = markupType === MarkupType.Segment ? 137 : 103;
        // 字号为 103 大小时，背景的左右 padding 比例
        const paddingHRatio = 70 / baseFontSize;
        const paddingH = Math.floor(label.background ? paddingHRatio * (fontSize || label.fontSize) : 0);
        // 背景的上下 padding 比例
        let paddingVRatio = 10 / baseFontSize;
        if (markupType === MarkupType.Segment) {
            paddingVRatio = 28 / baseFontSize;
        }
        const paddingV = Math.floor(label.background ? paddingVRatio * (fontSize || label.fontSize) : 0);
        return {
            paddingH,
            paddingV,
        };
    }

    static getRoundWeight(layer: ILayerProps) {
        const { label } = layer.data as IMarkupLayerProps;
        if (!label.background) return 0;
        let roundWeight = label.height;
        const markupType = getLayerRuntimeValue<MarkupType>(layer, 'markupType');
        if (markupType === MarkupType.Segment) {
            // roundWeight = Math.ceil((45 / 208) * label.height);
            // 减去上下 padding 32
            roundWeight = Math.ceil((45 / (208 - 32)) * label.height);
        }
        return roundWeight;
    }

    static getMargin(layer: ILayerProps, width: number, fontSize?: number, fillWidth = 0) {
        const { label } = layer.data as IMarkupLayerProps;
        if (!LabelKit.getRenderText(label)) return { marginH: 0, marginV: 0 };
        const markupType = getLayerRuntimeValue<MarkupType>(layer, 'markupType');

        // 尺寸标注， 1000px 宽度，margin 为 24px
        let marginRatioH = 24 / 1000;
        let marginRatioV = 24 / 1000;
        // 指引线，1000px 宽度，margin 有背景 49px，无背景 68px
        let offsetMarginH = 0;
        if (markupType === MarkupType.Segment) {
            marginRatioH = (label.background ? 49 : 68) / 1000;
            marginRatioV = (label.background ? 49 : 68) / 1000;
            offsetMarginH = fillWidth / 2;
        }
        const marginH = Math.floor(width * marginRatioH);
        const marginV = Math.floor(width * marginRatioV);
        // lineHeight 的上下间隙
        const baseFontSize = markupType === MarkupType.Segment ? 137 : 103;
        const offsetPadding = Math.floor(label.background ? 0 : (16 / baseFontSize) * (fontSize || label.fontSize));

        return {
            marginH: marginH + offsetMarginH,
            marginV: marginV - offsetPadding,
        };
    }

    static getLabelRenderInfoByLayer(layer: ILayerProps) {
        const { markupType, label, fill, anchor, endPoint } = layer.data as IMarkupLayerProps;
        if (markupType === MarkupType.Segment) {
            const points = [anchor.points![0].x, 0];
            if (anchor.points![1]) {
                points.push(anchor.points![1].x);
            }
            const min = Math.min(...points);
            const max = Math.max(...points);
            let offset = 0;
            if (anchor.points![1] && endPoint.url) {
                // 计算从端点的边缘开始计算间距
                const scale = fill.width / MARKUP_POINT_SIZE;
                const height = endPoint.height * scale;
                offset = height / 2;
            }
            return LabelKit.getLabelRenderInfo(max - min, fill.width, layer, offset);
        }
        return LabelKit.getLabelRenderInfo(layer.width, fill.width, layer);
    }

    static getLabelRenderInfo(layerWidth: number, fillWidth: number, layer: ILayerProps, renderPositionOffset = 0) {
        const { label } = layer.data as IMarkupLayerProps;
        const { rotate } = label;
        const { width: labelWidth, height: labelHeight, paddingH, paddingV } = LabelKit.getLabelSize(layer);
        const { marginH, marginV } = LabelKit.getMargin(layer, layerWidth, undefined, fillWidth);
        // console.log({ labelWidth, labelHeight });
        const positions = getRotatePosition({
            left: 0,
            top: 0,
            width: labelWidth,
            height: labelHeight,
            rotate,
        });
        const minX = minBy(positions, (pos) => pos.x) as IPoint;
        const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
        const minY = minBy(positions, (pos) => pos.y) as IPoint;
        const maxY = maxBy(positions, (pos) => pos.y) as IPoint;

        const renderWidth = maxX.x - minX.x;
        const renderHeight = maxY.y - minY.y;
        const halfRenderHeight = renderHeight / 2;

        const offsetMargin = (renderPositionOffset || fillWidth / 2) + marginV;
        let renderPosition = offsetMargin;
        if (label.rotate) {
            renderPosition += halfRenderHeight - labelHeight / 2;
        }

        let renderAreaUp = 0;
        let renderAreaDown = 0;
        if (label.position === MarkupTextPositionType.Center) {
            renderAreaUp = Math.ceil(halfRenderHeight);
            renderAreaDown = -Math.ceil(halfRenderHeight);
        } else if (label.position === MarkupTextPositionType.Up) {
            renderAreaUp = Math.ceil(renderHeight + offsetMargin);
        } else if (label.position === MarkupTextPositionType.Down) {
            renderAreaDown = -Math.ceil(renderHeight + offsetMargin);
        }

        return {
            /** 真实宽，文字宽 + 加上 padding */
            labelWidth,
            /** 真实高，文字高 + 加上 padding */
            labelHeight,
            /** 渲染宽，真实宽 + 加上 rotate */
            renderWidth,
            /** 渲染高，真实高 + 加上 rotate */
            renderHeight,
            /** 水平 padding */
            paddingH,
            /** 垂直 padding */
            paddingV,
            /** 水平 margin */
            marginH,
            /** 垂直 margin */
            marginV,
            /** 垂直定位，top bottom 的定位值 */
            renderPosition,
            /** 渲染区域 */
            renderArea: {
                /** 渲染占宽，渲染宽 + 双边 margin */
                width: renderWidth + marginH * 2,
                /** 渲染占上部分高 */
                up: renderAreaUp,
                /** 渲染占下部分高 */
                down: renderAreaDown,
            },
        };
    }

    static getRenderText(label: IMarkupLabel) {
        if (label.type === MarkupLabelType.custom) {
            return label.text.trim();
        }
        if (!label.units.length) return `${label.num}`;
        const sortList = [MarkupLabelUnit.mm, MarkupLabelUnit.cm, MarkupLabelUnit.inch, MarkupLabelUnit.m];
        const conversionFactors = {
            [MarkupLabelUnit.mm]: 1, // 毫米为最小单位
            [MarkupLabelUnit.cm]: 10, // 1 厘米 = 10 毫米
            [MarkupLabelUnit.m]: 1000, // 1 米 = 1000 毫米
            [MarkupLabelUnit.inch]: 25.4, // 1 英寸 = 25.4 毫米
        };
        const getMinUnit = () => {
            const resList: MarkupLabelUnit[] = [];
            const res = new Set(label.units);
            let resUnit = sortList[0];
            for (let i = sortList.length - 1; i >= 0; i--) {
                const element = sortList[i];
                if (res.has(element)) {
                    resList.unshift(element);
                    resUnit = element;
                }
            }
            return { resUnit, resList };
        };
        const { resUnit, resList } = getMinUnit();
        const mmValue = label.num * conversionFactors[resUnit];
        const result = {
            [MarkupLabelUnit.mm]: mmValue,
            [MarkupLabelUnit.cm]: parseFloat((mmValue / conversionFactors.cm).toFixed(2)),
            [MarkupLabelUnit.m]: parseFloat((mmValue / conversionFactors.m).toFixed(2)),
            [MarkupLabelUnit.inch]: parseFloat((mmValue / conversionFactors.inch).toFixed(2)),
        };
        const res: string[] = [];
        for (let i = 0; i < resList.length; i++) {
            const element = resList[i];
            res.push(`${result[element]} ${element}`);
        }
        return res.join('/');
    }

    getFontSize(layer: ILayerProps, fontSize: number, width: number): ISize & { fontSize: number } {
        if (fontSize <= this.MIN_FONT_SIZE) {
            fontSize = this.MIN_FONT_SIZE;
        }
        const { label } = layer.data as IMarkupLayerProps;
        const size = this.getSize(layer, fontSize);
        // console.log('getFontSize => ', fontSize, size);
        const { paddingH } = LabelKit.getPadding(layer, fontSize);
        const labelWidth = size.width + paddingH * 2;
        if (labelWidth <= width || fontSize === this.MIN_FONT_SIZE) {
            return {
                fontSize,
                ...size,
            };
        }
        return this.getFontSize(layer, Math.max(fontSize - 1, this.MIN_FONT_SIZE), width);
    }

    getSize(layer: ILayerProps, fontSize?: number) {
        const { label } = layer.data as IMarkupLayerProps;
        if (!this.dom) {
            this.create();
        }
        let width = 0;
        let height = 0;
        if (this.dom) {
            const attrs = LabelKit.getCommonAttrStyle(layer, fontSize);
            Object.keys(attrs).forEach((key) => {
                if (this.dom) this.dom.style[key] = attrs[key];
            });
            this.dom.style.padding = '0';
            this.dom.innerText = LabelKit.getRenderText(label);
            const bound = this.dom.getBoundingClientRect();
            width = Math.ceil(bound.width || Math.abs(bound.right - bound.left));
            height = Math.ceil(bound.height || Math.abs(bound.top - bound.bottom));
        }
        return {
            width,
            height,
        };
    }

    static getCommonAttrStyle(layer: ILayerProps, fontSize?: number) {
        const { label } = layer.data as IMarkupLayerProps;
        const fontFamilyMap = layer.__runtime?.fontFamilyMap || {};
        // console.log('getCommonAttrStyle', cloneDeep(layer));
        return {
            fontSize: `${fontSize || label.fontSize}px`,
            fontFamily: fontFamilyMap[label.fontFamily],
            lineHeight: `${label.lineSpacing}`,
            letterSpacing: `${label.letterSpacing}px`,
            fontWeight: label.bold ? 'bold' : 'normal',
            fontStyle: label.italic ? 'italic' : 'normal',
            // whiteSpace: 'pre-line',
            whiteSpace: 'nowrap',
            margin: '0',
        };
    }

    /** 获文字标签样式 */
    static getRenderStyle(layer: ILayerProps) {
        const { label } = layer.data as IMarkupLayerProps;
        return {
            ...LabelKit.getCommonAttrStyle(layer),
            boxSizing: 'border-box',
            color: label.color,
            position: 'absolute',
            zIndex: 1,
            background: label.background,
            borderRadius: label.roundWeight,
        } as React.CSSProperties;
    }

    destroy() {
        this.dom?.remove();
        this.dom = null;
    }
}

export default LabelKit;
